import type { ReactNode } from 'react';

/**
 * The frame a screen prints inside when it is saved as a PDF.
 *
 * On screen it is invisible: every part is `display: block`, so the children
 * flow exactly as they would without it. In print it becomes a table whose
 * header and footer rows are blank spacers, and the real header and footer are
 * fixed to the top and bottom of the paper. A fixed element repeats on every
 * printed page, and a table's `thead`/`tfoot` repeat too, so each page gets the
 * brand mark above its content and the footer below without overlap. That is
 * the only way a browser offers to put something on every page.
 *
 * `@page` in `index.css` sets the paper margin to zero, which is also what
 * stops the browser writing the URL, the date and the page title into the
 * margins — the header here is the only header the PDF has.
 */
export default function PrintFrame({
  title,
  subtitle,
  lines = [],
  children,
}: {
  /** The report's name, e.g. "Analytics report". */
  title: string;
  /** The window it covers. */
  subtitle: string;
  /** Anything else the header should say: filters, the generated-at stamp. */
  lines?: string[];
  children: ReactNode;
}) {
  return (
    <>
      <div className="print-frame-header" aria-hidden="true">
        <img src="/images/mds-logo.png" alt="" className="print-frame-logo" />
        <div className="print-frame-titles">
          <span className="print-frame-title">{title}</span>
          <span className="print-frame-subtitle">{subtitle}</span>
          {lines.length > 0 && <span className="print-frame-line">{lines.join(' · ')}</span>}
        </div>
      </div>
      <div className="print-frame-footer" aria-hidden="true">
        <span>Minch Digital Square · {title}</span>
        <span>{subtitle}</span>
      </div>

      <table className="print-frame">
        <thead>
          <tr>
            <td>
              <div className="print-frame-space-top" />
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{children}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>
              <div className="print-frame-space-bottom" />
            </td>
          </tr>
        </tfoot>
      </table>
    </>
  );
}
